import { useState } from 'react'
import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'

export default function LoginPage() {
  const { signInWithGoogle, signInWithEmail } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [forgotMode, setForgotMode] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)

  async function handleEmailSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error } = await signInWithEmail(email, password)
    if (error) setError(error.message)
    setLoading(false)
  }

  async function handleForgotSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/',
    })
    if (error) setError(error.message)
    else setForgotSent(true)
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-surface-container-low flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <img src="/placed-logo.svg" alt="Placed" className="h-20 mx-auto mb-4" style={{ display: 'block' }} />
          <p className="text-sm text-on-surface-variant tracking-wide">Rescue management, simplified</p>
        </div>

        <Button onClick={signInWithGoogle} variant="secondary" size="lg" className="w-full">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
            <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-outline-variant" />
          <span className="text-[0.6875rem] text-on-surface-variant tracking-wider uppercase font-semibold">or</span>
          <div className="flex-1 h-px bg-outline-variant" />
        </div>

        {forgotMode ? (
          forgotSent ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-on-surface">
                Check your inbox — we sent a password reset link to <strong>{email}</strong>.
              </p>
              <button
                onClick={() => { setForgotMode(false); setForgotSent(false); setError('') }}
                className="text-sm text-primary font-semibold"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <p className="text-sm text-on-surface-variant">
                Enter your email and we'll send you a link to reset your password.
              </p>
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              {error && <p className="text-sm text-secondary">{error}</p>}
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
              <button
                type="button"
                onClick={() => { setForgotMode(false); setError('') }}
                className="block w-full text-center text-sm text-on-surface-variant"
              >
                Back to sign in
              </button>
            </form>
          )
        ) : (
          <>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              {error && <p className="text-sm text-secondary">{error}</p>}
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Loading...' : 'Sign In'}
              </Button>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setError('') }}
                  className="text-xs text-primary font-semibold hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </form>

            <p className="text-center text-xs text-on-surface-variant leading-relaxed">
              Placed accounts are invite-only. Rescue staff sign in with the email
              their admin invited. Adopters:{' '}
              <a href="/adopt" className="text-primary font-semibold">
                browse adoptable animals
              </a>{' '}
              to submit an application.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
